import React, { useEffect, useRef, useState } from "react";
import LBMCEditor from "./LBMCEditor";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { Card } from "primereact/card";
import ReactMarkdown from "react-markdown";
import { Divider } from "primereact/divider";
import { updateProfileIntroduction } from "../Services/UpdateService";
import useAccessToken from "../Custom/useAccessToken";
import { Toast } from "primereact/toast";

interface AboutIntroProps {
  introduction: string | undefined;
  isOwnProfile?: boolean;
}

const AboutIntro: React.FC<AboutIntroProps> = ({
  introduction,
  isOwnProfile,
}) => {
  const { accessToken, thinkAccessToken } = useAccessToken();
  const [visible, setVisible] = useState(false);
  const [editorValue, setEditorValue] = useState<string>("");
  const [editMode, setEditMode] = useState(false);
  const [introState, setIntroState] = useState<string>(introduction || "");
  const toast = useRef<any>(null);

  // Sync prop introduction if it changes
  useEffect(() => {
    setIntroState(introduction || "");
  }, [introduction]);

  useEffect(() => {}, [accessToken, thinkAccessToken]);

  const MarkdownLink = (props: React.ComponentProps<"a">) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {props.children}
    </a>
  );

  const customHeader = (
    <div className="flex align-items-center gap-2 px-3">
      <span className="font-medium text-900 text-lg">
        {editMode ? "Edit Introduction" : "Add Introduction"}
      </span>
    </div>
  );

  const onSaveDescription = async () => {
    try {
      const result = await updateProfileIntroduction(accessToken, editorValue);
      if (result === true) {
        setIntroState(editorValue);
        setVisible(false);
        setEditMode(false);
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Introduction saved successfully",
          life: 3000,
        });
      } else {
        setIntroState(introduction || "");
      }
    } catch (error) {
      setIntroState(introduction || "");
    }
  };
  return (
    <>
      <Toast ref={toast} baseZIndex={5000} />
      <Card className="mb-3 shadow-1 about-intro-card">
        <div className="flex align-items-center justify-content-between w-full gap-1">
          <div style={{ flex: 1 }} className="markdown-content">
            {introState && introState.trim() !== "" ? (
              <ReactMarkdown
                components={{
                  a: MarkdownLink,
                }}
              >
                {introState}
              </ReactMarkdown>
            ) : (
              <span className="text-500 text-sm">
                Share a brief introduction about yourself, professional
                interests, or what excites you about working at LBMC.
              </span>
            )}
          </div>
          {isOwnProfile && (
            <Button
              icon="pi pi-pencil"
              className="p-button-text p-button shadow-none"
              onClick={() => {
                setEditMode(true);
                setEditorValue(introState);
                setVisible(true);
              }}
              tooltip="Edit Introduction"
            />
          )}
        </div>
      </Card>
      <Sidebar
        header={customHeader}
        visible={visible}
        position="right"
        onHide={() => {
          setVisible(false);
          setEditMode(false);
        }}
        showCloseIcon
        blockScroll
        style={{ width: "55vw" }}
      >
        <Divider />
        <div className="flex flex-column h-full p-0">
          <div className="flex flex-column flex-1 pt-3">
            <LBMCEditor
              className="w-full mb-6"
              editorId="about-intro-editor"
              value={editorValue}
              onBlur={(e) => setEditorValue(e)}
              accessToken={thinkAccessToken}
            />
            <div className="flex align-items-center gap-3 mt-4">
              <Button
                label={editMode ? "Save introduction" : "Add introduction"}
                className="p-button p-component p-button-sm"
                icon={editMode ? "pi pi-save" : "pi pi-plus"}
                onClick={onSaveDescription}
              />
              <Button
                label="Cancel"
                className="p-button p-component p-button-outlined p-button-sm"
                onClick={() => setVisible(false)}
              />
            </div>
          </div>
        </div>
      </Sidebar>
    </>
  );
};

export default AboutIntro;
